import { AHWidgetTitle, Box, useWidgetOptions } from "arkh";
import React, { useCallback, useState } from "react";

import { WidgetAddItemButton } from "./WidgetAddItemButton";
import { BaseWidgetProps } from "../BaseProps";

import { useEvent } from "../useEvent";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscribeToInvalidationState,
} from "react-relay";
import { WidgetTodoOverviewQuery } from "./__generated__/WidgetTodoOverviewQuery.graphql";
import { WidgetTodoOverview_widget$key } from "./__generated__/WidgetTodoOverview_widget.graphql";

import { WidgetTodoOverviewContentMedium } from "./WidgetTodoOverviewContentMedium";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";
import { useSetWidgetFocus } from "../useSetFocus";
import { WidgetTodoOverviewPreview_widget$key } from "./__generated__/WidgetTodoOverviewPreview_widget.graphql";
import { WidgetTodoOverviewContentMediumGrouped } from "./WidgetTodoOverviewContentMediumGrouped";

export const WidgetTodoOverview = (
  props: {
    widget: WidgetTodoOverview_widget$key;
  } & BaseWidgetProps
) => {
  const widget = useFragment(
    graphql`
      fragment WidgetTodoOverview_widget on WidgetTodoOverview {
        id
        title
        category {
          store {
            myRole
          }
          id
          title
          integration
        }
        teams {
          id
        }
      }
    `,
    props.widget
  );
  const [fetchKey, setFetchKey] = React.useState(0);
  useSubscribeToInvalidationState([widget.id], () => {
    setFetchKey((fk) => fk + 1);
  });
  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);
  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const { background } = useWidgetOptions();
  const onSelectTodo = useEvent((todoId: string) => {
    if (props.mode === "FOCUS" && props.highlightedItemId !== todoId) {
      setFocus(todoId);
    } else {
      if (widget.category) {
        props.onEvent?.({
          kind: "Todo",
          todoId,
          widgetId: widget.id,
          categoryId: widget.category?.id,
        });
      }
    }
  });
  const onEvent = props.onEvent;
  const interactive = props.interactive;
  const onRequestAddTodo = useCallback(
    (categoryId: string, categoryTitle: string, teamIds: string[]) =>
      onEvent?.({
        kind: "TodoCreate",
        categoryId,
        teamIds,
        categoryTitle,
      }),
    [onEvent]
  );

  const onPressAdd = useCallback(() => {
    if (!widget.category || interactive === false) {
      return;
    }
    onRequestAddTodo(
      widget.category.id,
      widget.category.title,
      widget?.teams?.map((t) => t.id) ?? []
    );
  }, [interactive, onRequestAddTodo, widget.category, widget.teams]);
  const [shouldUsePollFetchKey, setShouldUsePollFetchKey] = useState(true);
  const [ourPollFetchKey, setOurPollFetchKey] = useState("");
  if (
    ourPollFetchKey !== props.pollFetchKey &&
    shouldUsePollFetchKey &&
    props.pollFetchKey
  ) {
    setOurPollFetchKey(props.pollFetchKey);
  }

  const onShouldUsePollFetchKeyChange = useCallback(
    (shouldUsePollFetchKey: boolean) => {
      setShouldUsePollFetchKey(shouldUsePollFetchKey);
    },
    []
  );

  const fk = fetchKey + ":" + (props.fetchKey ?? "") + ":" + ourPollFetchKey;
  const query = useLazyLoadQuery<WidgetTodoOverviewQuery>(
    graphql`
      query WidgetTodoOverviewQuery(
        $widgetId: ID!
        $first: Int
        $after: String
      ) {
        widgetTodoOverview(id: $widgetId) {
          ...WidgetTodoOverviewContentMediumPaginated
          todoContent: content {
            ... on WidgetTodoOverviewContentMediumGrouped {
              __typename
              ...WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped
            }
            ... on WidgetTodoOverviewContentMedium {
              __typename
            }
          }
        }
      }
    `,
    {
      widgetId: widget.id,
    },
    {
      fetchKey: fk,
      fetchPolicy: "store-and-network",
    }
  );

  if (!query.widgetTodoOverview) {
    return null;
  }

  return (
    <Box style={{ flex: 1, backgroundColor: background }} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      {query.widgetTodoOverview.todoContent.__typename ===
      "WidgetTodoOverviewContentMedium" ? (
        <WidgetTodoOverviewContentMedium
          shouldDimInactive={
            !(props.idiom === "TOUCH" && props.mode === "FOCUS")
          }
          isFocused={props.isFocused}
          interactive={props.interactive}
          highlightedItemId={props.highlightedItemId}
          onPressItem={onSelectTodo}
          setScroll={setScroll}
          widget={query.widgetTodoOverview}
          setShouldPoll={onShouldUsePollFetchKeyChange}
          enableScrollToTopTimeout
        />
      ) : query.widgetTodoOverview.todoContent.__typename ===
        "WidgetTodoOverviewContentMediumGrouped" ? (
        <WidgetTodoOverviewContentMediumGrouped
          shouldDimInactive={
            !(props.idiom === "TOUCH" && props.mode === "FOCUS")
          }
          isFocused={props.isFocused}
          content={query.widgetTodoOverview.todoContent}
          highlightedItemId={props.highlightedItemId}
          onPressItem={onSelectTodo}
          setScroll={setScroll}
          interactive={interactive}
        />
      ) : null}
      {widget.category?.store?.myRole === "READ_ONLY" ||
      props.idiom === "STATIC" ||
      props.idiom === "TOUCH_READONLY" ||
      widget.category?.integration ? null : (
        <WidgetAddItemButton onPress={onPressAdd} />
      )}
    </Box>
  );
};

function noOp() {}
export function WidgetTodoOverviewPreview(
  props: {
    widget: WidgetTodoOverviewPreview_widget$key;
  } & BaseWidgetProps
) {
  const widget = useFragment(
    graphql`
      fragment WidgetTodoOverviewPreview_widget on WidgetTodoOverview {
        id
        title
        category {
          id
          title
        }
        teams {
          id
        }
        content {
          ... on WidgetTodoOverviewContentMediumGrouped {
            __typename
            ...WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped
          }
          ... on WidgetTodoOverviewContentMedium {
            __typename
          }
        }
        ...WidgetTodoOverviewContentMediumPaginated
      }
    `,
    props.widget
  );

  return (
    <Box style={{ flex: 1 }} color="surface">
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      {widget.content.__typename === "WidgetTodoOverviewContentMedium" ? (
        <WidgetTodoOverviewContentMedium
          isFocused={false}
          shouldDimInactive={true}
          interactive={false}
          onPressItem={noOp}
          setScroll={noOp}
          widget={widget}
        />
      ) : widget.content.__typename ===
        "WidgetTodoOverviewContentMediumGrouped" ? (
        <WidgetTodoOverviewContentMediumGrouped
          onPressItem={noOp}
          isFocused={false}
          shouldDimInactive={true}
          content={widget.content}
          interactive={false}
          setScroll={noOp}
        />
      ) : null}
    </Box>
  );
}

export { WidgetTodoOverviewContentMedium as WidgetTodoOverviewListMedium };
